import type { MasterSettings, Project, Track, VoiceEvent } from '../types';

/**
 * The mixer graph.
 *
 * Built once per context (live or offline) and then mutated by `syncGraph`
 * whenever project settings change, so parameter tweaks never re-create nodes
 * mid-playback.
 */

export interface TrackNodes {
  input: GainNode;
  filter: BiquadFilterNode;
  shaper: WaveShaperNode;
  shaperMix: GainNode;
  dryMix: GainNode;
  panner: StereoPannerNode;
  volume: GainNode;
  reverbSend: GainNode;
  delaySend: GainNode;
  analyser: AnalyserNode;
}

export interface MixerGraph {
  ctx: BaseAudioContext;
  tracks: Map<string, TrackNodes>;
  master: GainNode;
  limiter: DynamicsCompressorNode;
  /** Final soft clipper — the thing that actually guarantees no overs. */
  ceiling: WaveShaperNode;
  analyser: AnalyserNode;
  reverb: ConvolverNode;
  reverbReturn: GainNode;
  delay: DelayNode;
  delayFeedback: GainNode;
  delayReturn: GainNode;
  destination: AudioNode;
}

function makeCurve(amount: number): Float32Array<ArrayBuffer> {
  const n = 1024;
  const curve = new Float32Array(n);
  const k = amount * 60;
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    curve[i] = ((1 + k) * x) / (1 + k * Math.abs(x));
  }
  return curve;
}

/**
 * Transparent below -0.9 dBFS, asymptotic to 1.0 above it. The compressor ahead
 * of it is a limiter in name only — with a 2 ms attack it lets transients
 * through above the ceiling — and a bounce that clips is a bounce you have to
 * redo. Samples beyond ±1 land on the curve's endpoints, so this is also a
 * hard guarantee, not just a taper.
 */
function softClipCurve(): Float32Array<ArrayBuffer> {
  const n = 2048;
  const curve = new Float32Array(n);
  const knee = 0.9;
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    const a = Math.abs(x);
    const y = a <= knee ? a : knee + (1 - knee) * Math.tanh((a - knee) / (1 - knee));
    curve[i] = Math.sign(x) * y;
  }
  return curve;
}

export function makeImpulse(ctx: BaseAudioContext, seconds: number, decay: number): AudioBuffer {
  const sr = ctx.sampleRate;
  const len = Math.max(1, Math.floor(sr * seconds));
  const buf = ctx.createBuffer(2, len, sr);
  for (let c = 0; c < 2; c++) {
    const d = buf.getChannelData(c);
    for (let i = 0; i < len; i++) {
      const t = i / len;
      d[i] = (Math.random() * 2 - 1) * Math.pow(1 - t, decay) * (1 - t * 0.2);
    }
  }
  return buf;
}

export function createGraph(ctx: BaseAudioContext, destination: AudioNode): MixerGraph {
  const master = ctx.createGain();
  const limiter = ctx.createDynamicsCompressor();
  limiter.ratio.value = 20;
  limiter.attack.value = 0.002;
  limiter.release.value = 0.12;
  limiter.knee.value = 6;

  const analyser = ctx.createAnalyser();
  analyser.fftSize = 1024;

  const reverb = ctx.createConvolver();
  reverb.buffer = makeImpulse(ctx, 2, 3);
  const reverbReturn = ctx.createGain();
  reverbReturn.gain.value = 1;
  reverb.connect(reverbReturn).connect(master);

  const delay = ctx.createDelay(4);
  delay.delayTime.value = 0.25;
  const delayFeedback = ctx.createGain();
  delayFeedback.gain.value = 0.35;
  const delayReturn = ctx.createGain();
  delay.connect(delayFeedback).connect(delay);
  delay.connect(delayReturn).connect(master);

  const ceiling = ctx.createWaveShaper();
  ceiling.curve = softClipCurve();
  // Not oversampled on purpose: the resampling filters ring, and ringing on the
  // last stage puts samples back above 1.0 — exactly what this node exists to
  // prevent. The knee is gentle enough that aliasing isn't the bigger problem.
  ceiling.oversample = 'none';

  master.connect(limiter).connect(ceiling).connect(analyser).connect(destination);

  return {
    ctx,
    tracks: new Map(),
    master,
    limiter,
    ceiling,
    analyser,
    reverb,
    reverbReturn,
    delay,
    delayFeedback,
    delayReturn,
    destination,
  };
}

function createTrackNodes(graph: MixerGraph): TrackNodes {
  const ctx = graph.ctx;
  const input = ctx.createGain();
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 20000;

  const shaper = ctx.createWaveShaper();
  shaper.curve = makeCurve(0);
  shaper.oversample = '2x';
  const shaperMix = ctx.createGain();
  shaperMix.gain.value = 0;
  const dryMix = ctx.createGain();
  dryMix.gain.value = 1;

  const panner = ctx.createStereoPanner();
  const volume = ctx.createGain();
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 256;

  const reverbSend = ctx.createGain();
  reverbSend.gain.value = 0;
  const delaySend = ctx.createGain();
  delaySend.gain.value = 0;

  input.connect(filter);
  filter.connect(dryMix).connect(panner);
  filter.connect(shaper).connect(shaperMix).connect(panner);
  panner.connect(volume);
  volume.connect(analyser);
  volume.connect(graph.master);
  volume.connect(reverbSend).connect(graph.reverb);
  volume.connect(delaySend).connect(graph.delay);

  return {
    input,
    filter,
    shaper,
    shaperMix,
    dryMix,
    panner,
    volume,
    reverbSend,
    delaySend,
    analyser,
  };
}

export function trackNodes(graph: MixerGraph, id: string): TrackNodes {
  let n = graph.tracks.get(id);
  if (!n) {
    n = createTrackNodes(graph);
    graph.tracks.set(id, n);
  }
  return n;
}

export function syncTrack(graph: MixerGraph, track: Track, audible: boolean): void {
  const n = trackNodes(graph, track.id);
  const t = graph.ctx.currentTime;
  n.volume.gain.setTargetAtTime(audible ? track.volume : 0, t, 0.01);
  n.panner.pan.setTargetAtTime(track.pan, t, 0.01);
  if (track.filter === 'off') {
    n.filter.type = 'lowpass';
    n.filter.frequency.setTargetAtTime(20000, t, 0.01);
    n.filter.Q.setTargetAtTime(0.0001, t, 0.01);
  } else {
    n.filter.type = track.filter;
    n.filter.frequency.setTargetAtTime(track.cutoff, t, 0.01);
    n.filter.Q.setTargetAtTime(track.resonance, t, 0.01);
  }
  n.shaper.curve = makeCurve(track.drive);
  n.shaperMix.gain.setTargetAtTime(track.drive, t, 0.02);
  n.dryMix.gain.setTargetAtTime(1 - track.drive * 0.8, t, 0.02);
  n.reverbSend.gain.setTargetAtTime(track.sends.reverb, t, 0.02);
  n.delaySend.gain.setTargetAtTime(track.sends.delay, t, 0.02);
}

export function syncMaster(graph: MixerGraph, master: MasterSettings, bpm: number): void {
  const t = graph.ctx.currentTime;
  graph.master.gain.setTargetAtTime(master.volume, t, 0.02);
  graph.limiter.threshold.setTargetAtTime(master.ceiling, t, 0.02);
  graph.delay.delayTime.setTargetAtTime((60 / bpm) * master.delayTime, t, 0.05);
  graph.delayFeedback.gain.setTargetAtTime(master.delayFeedback, t, 0.02);
}

let lastReverb = '';
export function syncReverb(graph: MixerGraph, master: MasterSettings): void {
  const key = `${master.reverbSize}|${master.reverbDecay}`;
  if (key === lastReverb) return;
  lastReverb = key;
  graph.reverb.buffer = makeImpulse(graph.ctx, master.reverbSize, master.reverbDecay);
}

/** Track ids that own graph nodes without appearing in the project. */
const RESERVED_TRACK_IDS = new Set(['__preview']);

/**
 * Fades a removed track out before unwiring it.
 *
 * Voices are scheduled up to a lookahead window ahead of the playhead, so a
 * track deleted mid-bar usually has notes already committed to it. Ripping the
 * nodes out from under them cuts those dead, which clicks; a short ramp lets
 * them land.
 */
function releaseTrackNodes(ctx: BaseAudioContext, nodes: TrackNodes): void {
  const kill = () => {
    for (const node of [
      nodes.input,
      nodes.filter,
      nodes.shaper,
      nodes.shaperMix,
      nodes.dryMix,
      nodes.panner,
      nodes.volume,
      nodes.reverbSend,
      nodes.delaySend,
      nodes.analyser,
    ]) {
      try {
        node.disconnect();
      } catch {
        /* already detached */
      }
    }
  };

  // Offline renders build a fresh graph per bounce and never reach this with a
  // stale track, but if they did, a timer would never fire in render time.
  if (typeof OfflineAudioContext !== 'undefined' && ctx instanceof OfflineAudioContext) {
    kill();
    return;
  }
  nodes.volume.gain.setTargetAtTime(0, ctx.currentTime, 0.02);
  setTimeout(kill, 300);
}

export function syncGraph(graph: MixerGraph, project: Project, audibleFor: (t: Track) => boolean): void {
  for (const track of project.tracks) syncTrack(graph, track, audibleFor(track));

  // Drop nodes for tracks that no longer exist. They're silent once nothing
  // routes to them, but they stay wired to the master bus holding whatever
  // gain they had, and they accumulate for the life of the session.
  const live = new Set(project.tracks.map((t) => t.id));
  for (const [id, nodes] of graph.tracks) {
    if (live.has(id) || RESERVED_TRACK_IDS.has(id)) continue;
    // Remove from the map first so a second sync doesn't tear it down twice.
    graph.tracks.delete(id);
    releaseTrackNodes(graph.ctx, nodes);
  }

  syncMaster(graph, project.master, project.bpm);
  syncReverb(graph, project.master);
}

/** Instantiates one voice. Returns the source so callers can stop it early. */
export function playVoice(graph: MixerGraph, ev: VoiceEvent): AudioBufferSourceNode {
  const ctx = graph.ctx;
  const src = ctx.createBufferSource();
  src.buffer = ev.buffer;
  src.playbackRate.value = ev.playbackRate;

  const gain = ctx.createGain();
  const end = ev.time + ev.duration;
  // Linear, not equal-power: across a 1:1 chop the two sides of the crossfade
  // hold the same audio, and a + (1 - a) is the only pair that sums back to
  // the original. A minimum keeps the gain step itself from clicking.
  const fadeIn = Math.max(0.0003, Math.min(ev.fadeIn, ev.duration * 0.5));
  const fadeOut = Math.max(0.0003, Math.min(ev.fadeOut, ev.duration * 0.5));

  gain.gain.setValueAtTime(0, ev.time);
  gain.gain.linearRampToValueAtTime(ev.gain, ev.time + fadeIn);
  gain.gain.setValueAtTime(ev.gain, Math.max(ev.time + fadeIn, end - fadeOut));
  gain.gain.linearRampToValueAtTime(0, end);

  const pan = ctx.createStereoPanner();
  pan.pan.value = ev.pan;

  let head: AudioNode = src;
  if (ev.filter !== 'off') {
    const f = ctx.createBiquadFilter();
    f.type = ev.filter;
    f.frequency.value = ev.cutoff;
    f.Q.value = ev.resonance;
    src.connect(f);
    head = f;
  }
  head.connect(gain).connect(pan).connect(trackNodes(graph, ev.trackId).input);

  src.start(ev.time, ev.offset);
  src.stop(end + 0.005);
  src.onended = () => {
    try {
      pan.disconnect();
      gain.disconnect();
      src.disconnect();
    } catch {
      /* already torn down */
    }
  };
  return src;
}
